import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';

import { useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IluckydrawItem } from '@typings/store/luckydraw.interface';
import LuckyDrawModal from './detail/LuckyDrawModal';
import { getItemData } from '@store/firebase/store/parchase-item';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { defaultCurrencyState } from '@atom/setting/homeSettingState';
import { formatPrice } from '../component/WalletPrice';

type Props = {
  isSlider?: boolean;
};
function LuckyDrawSec({ isSlider = false }: Props) {
  const collectionPath = `${CollectionPath.LUCKYDRAW}`;
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<IluckydrawItem | any>();
  const [materialItems, setMaterialItems] = useState<
    Record<string, IstoreItem>
  >({});
  const defaultCurrency = useRecoilValue(defaultCurrencyState);
  const { moneyUnit, moneyImgURL } = defaultCurrency;

  const { data: luckyDrawList } = useCollection<IluckydrawItem>(
    collectionPath,
    {
      listen: true,
      orderBy: ['order', 'asc'],
    },
  );

  useEffect(() => {
    const fetchMaterialItems = async () => {
      if (!luckyDrawList) return;

      const materialItemsData: Record<string, IstoreItem> = {};

      for (const item of luckyDrawList) {
        if (item.materialItemID) {
          try {
            const materialData = await getItemData({
              itemID: item.materialItemID,
            });
            if (materialData) {
              materialItemsData[item.materialItemID] = materialData;
            }
          } catch (error) {
            console.error('재료 아이템 정보 로드 실패:', error);
          }
        }
      }

      setMaterialItems(materialItemsData);
    };

    fetchMaterialItems();
  }, [luckyDrawList]);

  const handleClick = useCallback((data: IluckydrawItem) => {
    setSelectedData(data);
    setOpenModal(true);
  }, []);

  const getModeBadge = (mode?: string) => {
    if (mode === 'useItem') return 'ITEM OPEN';
    if (mode === 'postUpload') return 'POST OPEN';
    return 'INSTANT';
  };

  return (
    <>
      <Container isSlider={isSlider}>
        {luckyDrawList && luckyDrawList.length > 0 ? (
          luckyDrawList.map((item) => (
            <PackageCard key={item.id} onClick={() => handleClick(item)} data-slide-item>
              <CardAccent />

              <CardBody>
                <CardLeft>
                  <PackageThumb src={item.imgURL}>
                    {!item.imgURL && (
                      <ThumbFallback>?</ThumbFallback>
                    )}
                  </PackageThumb>
                </CardLeft>

                <CardCenter>
                  <CardTitleRow>
                    <PackageTitle>{item.title}</PackageTitle>
                    <PackageBadge>{getModeBadge(item.openMode)}</PackageBadge>
                  </CardTitleRow>

                  <PackageDescription>
                    {item.contents || '행운을 시험해보세요'}
                  </PackageDescription>

                  {item.materialItemID && materialItems[item.materialItemID] && (
                    <MaterialRow>
                      {materialItems[item.materialItemID].imgURL && (
                        <MaterialImage
                          src={materialItems[item.materialItemID].imgURL}
                        />
                      )}
                      <MaterialName>
                        {materialItems[item.materialItemID].title}
                      </MaterialName>
                      <MaterialCount>x{item.materialCount || 1}</MaterialCount>
                    </MaterialRow>
                  )}
                </CardCenter>

                <CardRight>
                  <PriceTag>
                    {item.price && item.price > 0 ? (
                      <>
                        {moneyImgURL && <MoneyIcon src={moneyImgURL} alt={moneyUnit} />}
                        <PriceValue>{formatPrice(item.price)}</PriceValue>
                      </>
                    ) : (
                      <PriceValue>FREE</PriceValue>
                    )}
                  </PriceTag>
                </CardRight>
              </CardBody>
            </PackageCard>
          ))
        ) : (
          <EmptyState>등록된 뽑기가 없습니다</EmptyState>
        )}
      </Container>
      {openModal && (
        <LuckyDrawModal
          data={selectedData}
          closeModal={() => setOpenModal(false)}
        />
      )}
    </>
  );
}

export default LuckyDrawSec;

const Container = styled.div<{ isSlider?: boolean }>`
  width: 100%;
  display: ${({ isSlider }) => (isSlider ? 'flex' : 'flex')};
  flex-direction: ${({ isSlider }) => (isSlider ? 'row' : 'column')};
  gap: ${({ isSlider }) => (isSlider ? '16px' : '8px')};
  padding: ${({ isSlider }) => (isSlider ? '0' : '0')};
  flex-wrap: ${({ isSlider }) => (isSlider ? 'nowrap' : 'nowrap')};

  ${({ isSlider }) =>
    isSlider &&
    `
    > div {
      min-width: 340px;
      flex-shrink: 0;
    }
  `}
`;

const PackageCard = styled.div`
  position: relative;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--cardLineColor);
  border-radius: var(--cardBorderRadius);
  display: flex;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: var(--hoverCursorURL), pointer;

  &:hover {
    border-color: color-mix(in srgb, var(--primaryColor) 50%, transparent);
    background: color-mix(in srgb, var(--inkColor) 80%, var(--cardBackgroundColor));
  }
`;

const CardAccent = styled.div`
  width: 4px;
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    var(--primaryColor) 0%,
    color-mix(in srgb, var(--primaryColor) 40%, transparent) 100%
  );
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  min-height: 0;

  @media (max-width: 480px) {
    gap: 12px;
    padding: 12px;
  }
`;

const CardLeft = styled.div`
  flex-shrink: 0;
`;

const PackageThumb = styled.div<{ src?: string }>`
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: ${({ src }) => (src ? `url('${src}')` : 'var(--inkColor)')};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  border: 1px solid var(--lineColor);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }
`;

const ThumbFallback = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: var(--subTextColor);
  opacity: 0.5;
`;

const CardCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PackageTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--textColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PackageBadge = styled.span`
  padding: 2px 6px;
  background: color-mix(in srgb, var(--primaryColor) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--primaryColor) 40%, transparent);
  border-radius: 3px;
  font-size: 10px;
  color: var(--primaryColor);
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PackageDescription = styled.p`
  font-size: 12px;
  color: var(--subTextColor);
  line-height: 1.4;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MaterialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

const MaterialImage = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--lineColor);
  background: var(--inkColor);
`;

const MaterialName = styled.span`
  font-size: 11px;
  color: var(--subTextColor);
`;

const MaterialCount = styled.span`
  font-size: 11px;
  color: var(--primaryColor);
  font-weight: 700;
`;

const CardRight = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 80px;

  @media (max-width: 480px) {
    min-width: 60px;
  }
`;

const PriceTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: color-mix(in srgb, var(--buttonBackgroundColor) 20%, transparent);
  border: 1px solid color-mix(in srgb, var(--primaryColor) 30%, transparent);
  border-radius: 6px;

  @media (max-width: 480px) {
    padding: 4px 8px;
  }
`;

const MoneyIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
`;

const PriceValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--textColor);
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: var(--subTextColor);
  font-size: 14px;
`;
